import React, { useState } from 'react';
import styled from 'styled-components';
import { useRegistration } from '../../../contexts/RegistrationContext';

const StepContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
`;

const Title = styled.h2`
  color: #000;
  margin-bottom: 1rem;
  font-weight: 600;
`;

const Description = styled.p`
  color: #666;
  margin-bottom: 2rem;
`;

const ImportantNote = styled.div`
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 15px;
  padding: 1rem;
  margin-bottom: 2rem;
  color: #856404;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: #000;
  font-size: 0.9rem;
`;

const Input = styled.input`
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 25px;
  font-size: 1rem;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #000;
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
  }
  
  &.required {
    border-color: #000;
    background-color: #f8f9ff;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 2rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 25px;
  font-size: 1rem;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &.primary {
    background: #000;
    color: white;
    
    &:hover:not(:disabled) {
      background: #333;
      transform: translateY(-1px);
    }
  }
  
  &.secondary {
    background: #6c757d;
    color: white;
    
    &:hover:not(:disabled) {
      background: #5a6268;
      transform: translateY(-1px);
    }
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const Accordion = styled.div`
  border: 1px solid #ddd;
  border-radius: 15px;
  overflow: hidden;
  background: white;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
`;

const AccordionHeader = styled.button`
  width: 100%;
  padding: 1rem 1.5rem;
  background: #f8f9fa;
  border: none;
  text-align: left;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 1rem;
  font-weight: 500;
  color: #000;
  transition: all 0.2s ease;
  
  &:hover {
    background: #e9ecef;
  }
  
  &:focus {
    outline: none;
    background: #e9ecef;
  }
`;

const AccordionIcon = styled.span<{ isOpen: boolean }>`
  font-size: 1.2rem;
  transition: transform 0.2s;
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const AccordionContent = styled.div<{ isOpen: boolean }>`
  max-height: ${props => props.isOpen ? '1000px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease-out;
`;

const AccordionBody = styled.div`
  padding: 1.5rem;
  border-top: 1px solid #ddd;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const CompanyName: React.FC = () => {
  const { registrationData, setCurrentStep, saveCompanyName } = useRegistration();
  const [armenianName, setArmenianName] = useState(registrationData.company_name_armenian || '');
  const [englishName, setEnglishName] = useState(registrationData.company_name_english || '');
  const [russianName, setRussianName] = useState(registrationData.company_name_russian || '');
  const [isLoading, setIsLoading] = useState(false);
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate that at least one name is provided
    if (!armenianName.trim() && !englishName.trim() && !russianName.trim()) {
      alert('Please provide at least one company name (Armenian, English, or Russian).');
      return;
    }
    
    setIsLoading(true);

    try {
      await saveCompanyName({
        company_name_armenian: armenianName.trim() || undefined,
        company_name_english: englishName.trim() || undefined,
        company_name_russian: russianName.trim() || undefined,
      });
      setCurrentStep(3);
    } catch (error) {
      console.error('Failed to save company name:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setCurrentStep(1);
  };

  const handleAccordionToggle = () => {
    setIsAccordionOpen(!isAccordionOpen);
  };

  return (
    <StepContainer>
      <Title>Step 2: Company Name</Title>
      <Description>
        Provide your company name in at least one language. You can provide names in Armenian, English, and/or Russian.
      </Description>
      
      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label>Company Name in English</Label>
          <Input
            type="text"
            placeholder="Enter company name in English..."
            value={englishName}
            onChange={(e) => setEnglishName(e.target.value)}
          />
        </FormGroup>

        <ImportantNote>
          <strong>Important:</strong> You must provide at least one company name. 
          Armenian is preferred for official registration, but English and Russian are also acceptable.
        </ImportantNote>

        <Accordion>
          <AccordionHeader 
            type="button" 
            onClick={handleAccordionToggle}
            aria-expanded={isAccordionOpen}
          >
            I want to specify the name in Armenian/Russian myself
            <AccordionIcon isOpen={isAccordionOpen}>▼</AccordionIcon>
          </AccordionHeader>
          <AccordionContent isOpen={isAccordionOpen}>
            <AccordionBody>
              <FormGroup>
                <Label>Company Name in Armenian</Label>
                <Input
                  type="text"
                  placeholder="Enter company name in Armenian..."
                  value={armenianName}
                  onChange={(e) => setArmenianName(e.target.value)}
                />
              </FormGroup>

              <FormGroup>
                <Label>Company Name in Russian</Label>
                <Input
                  type="text"
                  placeholder="Enter company name in Russian..."
                  value={russianName}
                  onChange={(e) => setRussianName(e.target.value)}
                />
              </FormGroup>
            </AccordionBody>
          </AccordionContent>
        </Accordion>

        <ButtonGroup>
          <Button type="button" className="secondary" onClick={handleBack}>
            Back: Business Activity
          </Button>
          <Button type="submit" className="primary" disabled={isLoading}>
            {isLoading ? 'Saving...' : 'Next: Owner Information'}
          </Button>
        </ButtonGroup>
      </Form>
    </StepContainer>
  );
};

export default CompanyName;